// src/models/serviceModel.js
import api from '../services/api';




// Service Management Functions

// Get service payments for a building by floor
export const getServicesByFloor = async (buildingId, floor, year = null) => {
    try {
        const response = await api.get(`/services/building/${buildingId}/floor/${floor}`, { params: { year } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching floor service payments' };
    }
};

// Get all floors for a building with service payment statistics
export const getBuildingFloors = async (buildingId, year = null) => {
    try {
        const response = await api.get(`/services/building/${buildingId}/floors`, { params: { year } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching building floors' };
    }
};

// Get service payments for a specific apartment
export const getApartmentServices = async (apartmentId, year = null) => {
    try {
        const response = await api.get(`/services/apartment/${apartmentId}`, { params: { year } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching apartment service payments' };
    }
};

// Update service payment status
export const updateServicePayment = async (paymentId, updateData) => {
    try {
        const response = await api.put(`/services/${paymentId}`, updateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating service payment' };
    }
};

// Generate service payments for a building
export const generateServicePayments = async (generationData) => {
    try {
        const response = await api.post('/services/generate', generationData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error generating service payments' };
    }
};

// Add this function to src/models/serviceModel.js

// Generate a single service payment for an apartment
export const generateServicePayment = async (paymentData) => {
    try {
        const payload = {
            recipientType: 'apartment',
            apartmentId: paymentData.apartmentId,
            months: [paymentData.month],
            year: paymentData.year,
            amount: paymentData.amount,
            status: paymentData.status || 'unpaid',
            notes: paymentData.notes || 'Generated individually'
        };
        const response = await api.post('/services/generate', payload);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error generating service payment' };
    }
};
// Get service payment statistics
export const getServiceStats = async (buildingId, year = null) => {
    try {
        const response = await api.get('/services/stats', { params: { buildingId, year } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching service statistics' };
    }
};

// Get service payment by ID
// Update getServicePaymentById function in serviceModel.js without fake data
export const getServicePaymentById = async (id) => {
    try {
        const response = await api.get(`/services/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching payment details' };
    }
};

// Helper Functions

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Format date
export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(date);
};

// Get payment status color
export const getPaymentStatusColor = (isPaid, isDarkMode = false) => {
    if (isPaid) {
        return isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800';
    } else {
        return isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800';
    }
};

// Get month name
export const getMonthName = (month) => {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return months[month - 1] || '';
};

// Validate service payment data
export const validateServicePayment = (paymentData) => {
    const errors = {};

    if (!paymentData.amount || paymentData.amount <= 0) {
        errors.amount = 'Amount must be greater than 0';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};




// Get all service payments with filters and pagination
export const getAllServicePayments = async (filters = {}) => {
    try {
        const response = await api.get('/services', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching service payments' };
    }
};

export default {
    getServicesByFloor,
    getBuildingFloors,
    getApartmentServices,
    updateServicePayment,
    generateServicePayments,
    getServiceStats,
    getServicePaymentById,
    getAllServicePayments,
    formatCurrency,
    formatDate,
    getPaymentStatusColor,
    getMonthName,
    validateServicePayment
};
